import {
  Button,
  Checkbox,
  Classes,
  Icon,
  Intent,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Switch
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Tooltip2 } from "@blueprintjs/popover2";
import { faPeopleArrows } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, isAfter, startOfDay } from "date-fns";
import { ButtonContainer, IDataTableColumn, showError, ToolTipButton } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import { SuiviReassignmentDialog } from "..";

import { DashboardApi, DashboardSuiviSiegeLieuSearch, EDashboardSuiviMode, SiegeLieuSuiviGridDto } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { CustomBulletList, LinkButton, SearchTablePage, ViewButton } from "../../../components";
import { useAuth, useDialog, useEventsContext } from "../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTabMessage, useTheme, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { Guid } from "../../../utils/guid";

const LegendHeaderContainer = styled.div`
  & > * + * {
    margin-left: 0.5rem;
  }
`;

const StyledSwitch = styled(Switch)`
  & .${Classes.SWITCH} {
    &:hover {
      input:checked ~ .${Classes.CONTROL_INDICATOR} {
        background: ${props => props.color};
      }
    }
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PopoverContent = styled.div`
  padding: 0.5rem;
`;

export interface IDashboardSuivisSiegeLieuProps {}

export const DashboardSuivisSiegeLieu: React.FunctionComponent<IDashboardSuivisSiegeLieuProps> = props => {
  const { t } = useTl();
  const { user, hasPermission } = useAuth();
  const { theme } = useTheme();
  const api = useApiService(DashboardApi);
  const apiFactory = useAbortableApiServiceFactory(DashboardApi);
  const lastAbortController = React.useRef<AbortController>();
  const [selectedIDs, setSelectedIDs] = React.useState<number[]>([]);

  const [mode, setMode] = React.useState<EDashboardSuiviMode>(EDashboardSuiviMode.ARealiser);
  const [displayHidden, setDisplayHidden] = React.useState(false);
  const searchFunction = React.useCallback(
    (sObj?: DashboardSuiviSiegeLieuSearch) => {
      sObj.hideMasquees = !displayHidden;
      sObj.mode = mode;
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.dashboardSearchSuivisSiegeLieu({ DashboardSuiviSiegeLieuSearch: sObj });
    },
    [apiFactory, displayHidden, mode]
  );

  const [rowLoading, setRowLoading] = React.useState(null);

  const { showDialogPromise } = useDialog();
  const { dispatchEvent } = useEventsContext();
  const marquerCommeFait = React.useCallback(
    async (idsuivi: number) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MarquerSuiviCommeFaitConfirmTitle),
        message: t(ETLCodes.MarquerSuiviCommeFaitConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading(idsuivi);
        await api.dashboardMarquerSuiviFait({
          idsuivi
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_SuivisSL");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );
  const masquerSuivi = React.useCallback(
    async (idsuivi: number) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MasquerSuiviConfirmTitle),
        message: t(ETLCodes.MasquerSuiviConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading(idsuivi);
        await api.dashboardMasquerSuivi({
          idsuivi
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_SuivisSL");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );

  const toggleSelection = React.useCallback(
    (item: SiegeLieuSuiviGridDto) => {
      if (!selectedIDs.includes(item.idsuivi)) {
        setSelectedIDs(ids => [...ids, item.idsuivi]);
      } else {
        setSelectedIDs(ids => ids.filter(id => id !== item.idsuivi));
      }
    },
    [selectedIDs]
  );

  const getActionValue = React.useCallback((row: SiegeLieuSuiviGridDto) => {
    if (!row.actionDateRealisation && !row.actionDateEcheance) return null;
    return format(!!row.actionDateRealisation ? row.actionDateRealisation : row.actionDateEcheance, "dd-MM-yyyy");
  }, []);

  const identifier = React.useMemo(() => Guid.newGuid(), []);
  const { openTabThenSendMessage } = useTabMessage(identifier);

  const onPersonneClick = React.useCallback((row: SiegeLieuSuiviGridDto) => {
    let url = "";

    if (row.idapprenant) url = `#${ERoutes.apprenant}/${row.idapprenant}/signaletique/edit`;
    if (row.idrepresentant) url = `#${ERoutes.representant}/${row.idrepresentant}/detail/edit`;
    if (row.idformateur) url = `#${ERoutes.formateur}/${row.idformateur}/signaletique/edit`;
    if (row.idtuteur) url = `#${ERoutes.tuteur}/${row.idtuteur}/detail/edit`;
    if (row.idcontact) url = `#${ERoutes.contacts}/${row.idcontact}/detail/edit`;
    if (row.idchefEntreprise) url = `#${ERoutes.chefEntreprise}/${row.idchefEntreprise}/detail/edit`;

    window.open(url, "_blank");
  }, []);

  const getForme = React.useCallback((row: SiegeLieuSuiviGridDto) => {
    if (row.idapprenant) return ETLCodes.Apprenant;
    if (row.idrepresentant) return ETLCodes.Representant;
    if (row.idformateur) return ETLCodes.Formateur;
    if (row.idtuteur) return ETLCodes.Tuteur;
    if (row.idcontact) return ETLCodes.Contact;
    if (row.idchefEntreprise) return ETLCodes.ChefEntreprise;
  }, []);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "checkboxes",
        autoFitContent: true,
        render: (item: SiegeLieuSuiviGridDto) => (
          <Checkbox
            checked={selectedIDs.includes(item.idsuivi)}
            onChange={e => {
              toggleSelection(item);
            }}
          />
        )
      },
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: SiegeLieuSuiviGridDto) => (
          <ButtonContainer>
            {user.iduser === row.realisateurAction && (
              <ToolTipButton
                tooltipPosition="bottom"
                minimal
                icon="tick"
                onClick={() => marquerCommeFait(row.idsuivi)}
                loading={!!rowLoading && rowLoading === row.idsuivi}
                disabled={!!rowLoading && rowLoading !== row.idsuivi}
                tooltip={t(ETLCodes.MarquerCommeFait)}
              />
            )}
            {!row.masquee && !!row.actionDateEcheance && isAfter(new Date(), row.actionDateEcheance) && (
              <ToolTipButton
                tooltipPosition="bottom"
                tooltip={t(ETLCodes.MasquerSuivi)}
                minimal
                icon="cross"
                onClick={() => masquerSuivi(row.idsuivi)}
                loading={!!rowLoading && rowLoading === row.idsuivi}
                disabled={!!rowLoading && rowLoading !== row.idsuivi}
              />
            )}
            <ViewButton
              minimal={true}
              onClick={() =>
                openTabThenSendMessage(
                  !!row.idlieuFormation
                    ? `#${ERoutes.lieuFormation}/${row.idlieuFormation}/suivi/view/${row.idsuivi}`
                    : `#${ERoutes.siegeSocial}/${row.idsiegeSocial}/suivi/view/${row.idsuivi}`,
                  "FromDashboard",
                  !!row.idlieuFormation && [{ key: "idsiegeSocial", value: row.idsiegeSocial.toString() }]
                )
              }
            />
          </ButtonContainer>
        )
      },
      {
        header: () => (
          <LegendHeaderContainer>
            <span>{t(ETLCodes.ActionARealiser)}</span>
            <Tooltip2
              content={
                <CustomBulletList
                  title={t(ETLCodes.Legende)}
                  items={[
                    {
                      text: t(ETLCodes.ActionRealisee),
                      color: theme.sucessColor
                    },
                    {
                      text: t(ETLCodes.DateEcheanceDepassee),
                      color: theme.dangerColor
                    },
                    {
                      text: t(ETLCodes.ActionNonRealisee),
                      color: theme.warningColor
                    }
                  ]}
                />
              }
              popoverClassName="bullet-list-popover"
              position="right"
            >
              <Icon icon="info-sign" style={{ cursor: "pointer" }} />
            </Tooltip2>
          </LegendHeaderContainer>
        ),
        fieldName: "actionARealiser",
        render: (row: SiegeLieuSuiviGridDto) => (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              color: !!row.actionDateRealisation
                ? theme.sucessColor
                : isAfter(startOfDay(new Date()), startOfDay(row.actionDateEcheance))
                ? theme.dangerColor
                : theme.warningColor
            }}
          >
            <Icon
              style={{ marginRight: ".5rem" }}
              icon={
                !!row.actionDateRealisation
                  ? "tick"
                  : isAfter(startOfDay(new Date()), startOfDay(row.actionDateEcheance))
                  ? "issue"
                  : "time"
              }
            />
            {getActionValue(row)}
          </span>
        )
      },
      {
        header: () => t(ETLCodes.DateSuivi),
        fieldName: "dateSuivi"
      },
      {
        header: () => t(ETLCodes.RealisateurSuivi),
        fieldName: "realisateurSuivi",
        render: (row: SiegeLieuSuiviGridDto) => (
          <LinkButton
            text={row.realisateurSuivi}
            onClick={() => window.open(`#${ERoutes.users}/${row.idrealisateurSuivi}/detail/view`, "_blank")}
          />
        )
      },
      {
        header: () => t(ETLCodes.SiegeSocial),
        fieldName: "siegeSocial",
        render: (row: SiegeLieuSuiviGridDto) => (
          <LinkButton
            text={row.siegeSocial}
            onClick={() => window.open(`#${ERoutes.siegeSocial}/${row.idsiegeSocial}/detail/view`, "_blank")}
          />
        )
      },
      {
        header: () => t(ETLCodes.LieuFormation),
        fieldName: "lieuFormation",
        render: (row: SiegeLieuSuiviGridDto) => (
          <LinkButton
            text={row.lieuFormation}
            onClick={() =>
              window.open(
                `#${ERoutes.lieuFormation}/${row.idlieuFormation}/detail/view?idsiegeSocial=${row.idsiegeSocial}`,
                "_blank"
              )
            }
          />
        )
      },
      {
        header: () => t(ETLCodes.Forme),
        fieldName: "forme",
        render: (row: SiegeLieuSuiviGridDto) => t(getForme(row))
      },
      {
        header: () => t(ETLCodes.Beneficiaire),
        fieldName: "personne",
        render: (row: SiegeLieuSuiviGridDto) => <LinkButton text={row.personne} onClick={() => onPersonneClick(row)} />
      }
    ],
    [
      getActionValue,
      getForme,
      marquerCommeFait,
      masquerSuivi,
      onPersonneClick,
      openTabThenSendMessage,
      rowLoading,
      selectedIDs,
      t,
      theme.dangerColor,
      theme.sucessColor,
      theme.warningColor,
      toggleSelection,
      user.iduser
    ]
  );

  const getCriteria = React.useCallback(async () => {
    return await api.dashboardGetSuiviSiegeLieuCriterias();
  }, [api]);

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  const [reassignmentDialogOpen, setReassignmentDialogOpen] = React.useState(false);
  const reassign = React.useCallback(() => {
    if (selectedIDs.length <= 0) {
      showError(t(ETLCodes.SelectAtLeastOne));
      return;
    }

    setReassignmentDialogOpen(true);
  }, [selectedIDs.length, t]);

  const onCloseReassignment = React.useCallback(() => {
    setReassignmentDialogOpen(false);
    dispatchEvent("SEARCH_TABLE_REFRESH");
  }, [dispatchEvent]);

  const rightElement = React.useMemo(
    () => (
      <ButtonsContainer>
        {hasPermission("SUIVI", "RW", "REASSIGNMENT") && (
          <Popover
            position="bottom"
            content={
              <PopoverContent>
                <Menu>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faPeopleArrows} />}
                    text={t(ETLCodes.ReattribuerSuivi)}
                    intent={Intent.PRIMARY}
                    onClick={() => reassign()}
                  />
                </Menu>
              </PopoverContent>
            }
          >
            <Button
              icon={IconNames.CHEVRON_DOWN}
              text={t(ETLCodes.GlobalActions)}
              minimal={true}
              intent={Intent.PRIMARY}
            ></Button>
          </Popover>
        )}
        <StyledSwitch
          color={theme.primaryColor}
          label={t(ETLCodes.AfficherMasquees)}
          checked={displayHidden}
          onChange={e => {
            setDisplayHidden(e.currentTarget.checked);
          }}
        ></StyledSwitch>
      </ButtonsContainer>
    ),
    [displayHidden, hasPermission, reassign, t, theme.primaryColor]
  );

  return (
    <>
      <RadioGroup
        selectedValue={mode}
        inline
        onChange={e => setMode(e.currentTarget.value as EDashboardSuiviMode.ARealiser)}
      >
        <Radio label={t(ETLCodes.ARealiser)} value={EDashboardSuiviMode.ARealiser} />
        <Radio label={t(ETLCodes.Demandé)} value={EDashboardSuiviMode.Demande} />
        <Radio label={t(ETLCodes.ASuivre)} value={EDashboardSuiviMode.ASuivre} />
      </RadioGroup>
      <SearchTablePage
        searchStateInitialSearch={false}
        withCard={false}
        columns={columns}
        getCriteriasFunction={getCriteria}
        searchFunction={searchFunction}
        onAbort={onAbort}
        sortKeys={{ dateSuivi: "DESC" }}
        rightElement={rightElement}
      />
      {reassignmentDialogOpen && (
        <SuiviReassignmentDialog
          dialogOpen={reassignmentDialogOpen}
          onClose={onCloseReassignment}
          idsSuivi={selectedIDs}
        />
      )}
    </>
  );
};
